/* eslint-disable no-unused-vars */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTest } from '../context/TestContext';
import { 
  TrashIcon, 
  ClockIcon, 
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import Sidebar from '../components/Sidebar';
import ConfirmModal from '../components/ConfirmModal';

const CreatedTests = () => {
  const navigate = useNavigate();
  const { createdTests, loadTests, deleteTest } = useTest();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [deleteTestId, setDeleteTestId] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    loadTests();
    // Load test results
    const loadTestResults = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/test-results', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        const data = await response.json();
        // Organize results by test ID
        const resultsByTest = data.reduce((acc, result) => {
          if (!acc[result.testId]) {
            acc[result.testId] = [];
          }
          acc[result.testId].push(result);
          return acc;
        }, {});
        setTestResults(resultsByTest);
      } catch (error) {
        console.error('Error loading test results:', error);
      }
    };
    loadTestResults();
  }, [loadTests]);

  const handleDeleteTest = async () => {
    if (await deleteTest(deleteTestId)) {
      setDeleteTestId(null);
    }
  };

  const handleTakeTest = (test) => {
    navigate('/test/take', { state: { test } });
  };

  const getLatestResult = (testId) => {
    if (!testResults[testId] || testResults[testId].length === 0) return null;
    return testResults[testId].sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    )[0];
  };

  return (
    <div className="min-h-screen bg-dark-bg flex">
      <Sidebar 
        isSidebarOpen={isSidebarOpen} 
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} 
      />

      <div className="flex-1">
        <main className={`transition-all duration-500 ease-in-out ${
          'ml-0 lg:ml-64'
        }`}>
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
              <button
                onClick={() => navigate('/test')}
                className="flex items-center text-gray-400 hover:text-white transition-colors mb-6"
              >
                <ArrowLeftIcon className="w-5 h-5 mr-2" />
                Back to Test Center
              </button>

              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-8">Created Tests</h1>

              <div className="grid grid-cols-1 gap-4">
                {createdTests.map(test => (
                  <div key={test._id} className="bg-dark-card rounded-lg p-4 sm:p-6 w-full">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-4">
                      <div className="flex-1 mb-4 sm:mb-0">
                        <h3 className="text-lg font-medium text-white mb-2">{test.title}</h3>
                        <div className="flex items-center text-sm text-gray-400">
                          <DocumentTextIcon className="w-4 h-4 mr-1" />
                          <span>{test.questions.length} questions</span>
                          <span className="mx-2">•</span>
                          <ClockIcon className="w-4 h-4 mr-1" />
                          <span>
                            {test.settings.timeLimit ? `${test.settings.timeLimit} mins` : 'No time limit'}
                          </span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleTakeTest(test)}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center"
                        >
                          <ArrowPathIcon className="w-4 h-4 mr-2" />
                          Take Again
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTestId(test._id);
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Latest Result Summary */}
                    {getLatestResult(test._id) && (
                      <div className="text-sm text-gray-400 mb-4">
                        <div className="flex items-center">
                          <span>Latest Score: {getLatestResult(test._id).score}%</span>
                          <span className="mx-2">•</span>
                          <span>Attempts: {test.timesAttempted}</span>
                        </div>
                      </div>
                    )}

                    {/* Expanded Test Details */}
                    {selectedTest?._id === test._id && (
                      <div className="mt-4 space-y-4 border-t border-gray-800/40 pt-4">
                        <h4 className="text-lg font-medium text-white mb-2">Questions & Answers</h4>
                        {test.questions.map((question, index) => {
                          const latestResult = getLatestResult(test._id);
                          const answer = latestResult?.answers?.find(a => a.questionIndex === index);

                          return (
                            <div key={index} className="bg-dark-hover rounded-lg p-4">
                              <div className="flex items-start justify-between mb-2">
                                <p className="text-white mb-2">{question.question}</p>
                                {answer && (
                                  answer.isCorrect ? (
                                    <CheckCircleIcon className="w-5 h-5 text-green-500 ml-2 flex-shrink-0" />
                                  ) : (
                                    <XCircleIcon className="w-5 h-5 text-red-500 ml-2 flex-shrink-0" />
                                  )
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                {question.options.map((option, optIndex) => (
                                  <div
                                    key={optIndex}
                                    className={`p-2 rounded ${
                                      answer?.answer === option.charAt(0)
                                        ? answer.isCorrect
                                          ? 'bg-green-500/10 text-green-400'
                                          : 'bg-red-500/10 text-red-400'
                                        : option.charAt(0) === question.correctAnswer
                                          ? 'bg-green-500/10 text-green-400'
                                          : 'bg-dark-bg text-gray-400'
                                    }`}
                                  >
                                    {option}
                                  </div>
                                ))}
                              </div>
                              {answer && !answer.isCorrect && (
                                <p className="text-sm text-gray-400 mt-2">
                                  <span className="text-green-400">Correct Answer:</span> {
                                    question.options.find(opt => opt.charAt(0) === question.correctAnswer)
                                  }
                                </p>
                              )}
                              <p className="text-sm text-gray-400 mt-2">{question.explanation}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteTestId !== null}
        onClose={() => setDeleteTestId(null)}
        onConfirm={handleDeleteTest}
        title="Delete Test"
        message="Are you sure you want to delete this test? This action cannot be undone."
      />
    </div>
  );
};

export default CreatedTests; 